import { Component, inject, input, Input, OnInit } from "@angular/core";
import { Offer } from "@models";
import { ActionButtonsComponent } from "../../action-buttons.component";
import { CryptowalletDetailsComponent } from "../../../main/components/wallet-edit/wallet-details/cryptowallet-details.component";
import { Tradable } from "@models/interfaces/tradable";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { Index } from "@models/asset/index/index.model";
import { IndexWallet } from "@models/asset/index/index_wallet";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { StockTestService } from "@main-services/stock-test.service";
import { InternalTransaction, SendTransaction } from "@models/transaction";
import { TRADE_TYPE } from "@models/trade-offer.model";
import { AccountService } from "../../../account/services/account.service";
import { TooltipCoinOptions, TooltipService, TooltipTradableAction } from "@main-services/tooltip.service";
import { UserService } from "@main-services/user.service";
import { TransferModalComponent } from "../../../main/components/step-summary/transfer-modal/transfer-modal.component";
import { ModalService } from "@core-services/modal.service";
import { TranslationService } from "@core-services/translation.service";
import { TradeSwapComponent } from "../../../trade/modals/trade-swap-modal/trade-swap.component";
import { TradeSavingsComponent } from "../../../trade/modals/trade-savings-modal/trade-savings-modal.component";
import { StockTestHelperService } from "@main-services/stock-test-helper.service";
import { TOKEN_BEST_ID } from "@helpers/constants";
import { Best2TermsModalComponent } from "../../../trade/modals/best-2-terms-modal/components/best-2-terms-modal.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { StakingFacade } from "@modules/staking/domain/+state/staking.facade";
import { filter, map, startWith, take } from "rxjs/operators";
import { Stakeable } from "@modules/staking/domain/entities/stakeable.model";
import {
	ButtonStatus,
	SendEntryPointService
} from "@modules/+new/payments/features/send/domain/send-entry-point.service";
import { combineLatest, EMPTY, Observable, of } from "rxjs";
import { StakedAssetFacade } from "@modules/staking/domain/+state/staked-asset.facade";
import { UserFiatFundsService } from "@modules/shared/services/user-fiat-funds.service";
import { FiatDepositEntryService } from "@modules/+new/payments/features/fiat-deposit/infrastructure/fiat-deposit-entry.service";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { CryptoDepositFacade } from "@modules/+new/payments/features/crypto-deposit/domain/+state/crypto-deposit.facade";
import { CryptoDepositTrackingFacade } from "@modules/+new/payments/features/crypto-deposit/domain/+state/crypto-deposit-tracking.facade";
import { CryptocoinsState } from "@modules/+new/payments/shared-payments/services/cryptocoins-state.service";
import { CheckoutEntryHelperService } from "@modules/checkout/services/checkout-entry-helper.service";
import { SendTrackingFacade } from "@modules/main/components/step-summary/sending-modal/services/send-tracking.facade";
import { LimitOrdersTrackingFacade } from "@modules/limit-orders/services/orders-rudderstack.facade";
import { OrderFlow } from "@modules/limit-orders/interfaces/limit-order-rudderstack.model";
import { LimitOrdersService } from "@modules/limit-orders/services/limit-orders.service";
import { ActionButtonsFacade } from "@modules/discover/domain/+state/action-buttons.facade";
import { MarginTradingFacade } from "@modules/margin-trading/services/margin-trading.facade";
import { FEAT_GATES, StatsigClientService } from "@core-services/statsig/statsig.service";
import { LeveragedToken } from "@models/asset/cryptocoins/currencies/leveraged_tokens";

@UntilDestroy()
@Component({
	selector: "tradable-action-buttons",
	templateUrl: "./tradable-action-buttons.component.html",
	styleUrls: ["./tradable-action-buttons.component.scss"],
	standalone: false
})
export class TradableActionButtonsComponent extends ActionButtonsComponent<TradableWallet> implements OnInit {
	@Input() public tradable: Tradable = null;
	@Input() public buttonActions: string[] = []; // Initiated as an empty array, buttonActions are derived directly from the asset-model
	@Input() public buttonMoreActions: string[] = []; // Used to include them into a single "..." (more) button.
	@Input("isAssetDetail") private isAssetDetail: boolean = false;

	private assetWalletsForTransfer: boolean = false;
	protected buttonActionsForMobile: string[] = [];
	public hasEnoughAssetsToSwap: boolean = false;

	public stakeable: Stakeable = null;

	public depositButtonData$: Observable<DepositButtonData> = EMPTY;

	public digiSunsetHideDetails = input<boolean>();

	protected readonly cryptoDepositTrackingFacade = inject(CryptoDepositTrackingFacade);
	protected readonly sendTrackingFacade = inject(SendTrackingFacade);
	protected readonly depositCryptoState = inject(CryptocoinsState);
	protected readonly limitOrdersService = inject(LimitOrdersService);
	protected readonly marginTradingFacade: MarginTradingFacade = inject(MarginTradingFacade);
	protected readonly statsigClientService: StatsigClientService = inject(StatsigClientService);

	public showRedDot$: Observable<boolean> = this.marginTradingFacade.showMarginTradingRedDot$;

	public constructor(
		protected readonly modalService: ModalService,
		protected readonly accountService: AccountService,
		protected readonly tradableWalletListService: TradableWalletListService,
		protected readonly translationService: TranslationService,
		protected readonly fiatWalletService: FiatWalletService,
		protected readonly tooltipService: TooltipService,
		protected readonly userService: UserService,
		protected readonly stockTestService: StockTestService,
		protected readonly stockTestHelperService: StockTestHelperService,
		protected readonly stakingFacade: StakingFacade,
		protected readonly userFiatFundsService: UserFiatFundsService,
		protected readonly fiatDepositEntryService: FiatDepositEntryService,
		protected readonly stakedAssetFacade: StakedAssetFacade,
		protected readonly sendEntryPoint: SendEntryPointService,
		protected readonly launchDarklyService: LaunchDarklyService,
		protected readonly checkoutEntryHelper: CheckoutEntryHelperService,
		protected readonly cryptoDepositFacade: CryptoDepositFacade,
		protected readonly limitOrdersTrackingFacade: LimitOrdersTrackingFacade,
		protected readonly actionButtonsFacade: ActionButtonsFacade
	) {
		super(
			accountService,
			modalService,
			userService,
			tooltipService,
			stockTestService,
			stockTestHelperService,
			launchDarklyService,
			fiatWalletService,
			checkoutEntryHelper
		);
	}

	public ngOnInit(): void {
		if (this.tradable) {
			this.updateAssetWalletsForTransfer();
			this.tradableWalletListService
				.getList()
				.pipe(untilDestroyed(this))
				.subscribe(() => this.updateAssetWalletsForTransfer());
		}
		this.stakedAssetFacade
			.getAdpStakeable$()
			.pipe(take(1))
			.subscribe((stakeable) => {
				this.stakeable = stakeable;
			});
		this.hasEnoughAssetsToSwap = this.tooltipService.hasOtherAssetsToSwap(this.tradable, this.fiat);
		this.depositButtonData$ = this.getDepositButtonData$();
	}

	public isAssetDetailPage(): boolean {
		return this.isAssetDetail;
	}

	public isGenericActionButton(button: string): boolean {
		return button !== "send" && button !== "transfer" && button !== "deposit"; // The actions listed here need special handling in terms of e.g. tooltips
	}

	public getActionButtons(): string[] {
		if (!this.tradable) {
			return [];
		}
		if (this.buttonActionsForMobile.length === 0) {
			// ActionButtons for Mobile are set once, when the tradable is initiated
			this.setButtonActionsForMobile();
		}

		// Change needed for UK
		if (this.isLeveragedToken()) {
			return ["sell"];
		}

		// Change needed for UK
		if (this.tradable.getId() === TOKEN_BEST_ID) {
			return ["sell", "swap"];
		}
		return this.isMobileWindow() && !this.isAssetDetailPage() ? this.buttonActionsForMobile : this.buttonActions; // In price-list and on price-detail page the savings button is always displayed
	}

	public isDepositActionButtonDisabled(tradable: Tradable): Observable<boolean> {
		return combineLatest([
			this.launchDarklyService.isActive$(LD_FLAG.DEPOSIT_MULTICHAIN),
			this.depositCryptoState.availableCryptoDepositList$
		]).pipe(
			map(([isEnabled, availableDepositCryptos]) => {
				if (isEnabled) {
					const availableDepositCrypto = availableDepositCryptos.find(
						(crypto) => crypto.cryptocoin_id.toString() === tradable.getId()
					);
					if (availableDepositCrypto) {
						return !(
							availableDepositCrypto.deposit_operational && !this.userService.isVerificationMandatory
						);
					}
					return true;
				}
				return this.tooltipService.isDisabled("deposit", this.tradable, this.fiat, this.wallet);
			})
		);
	}

	private setButtonActionsForMobile(): void {
		this.buttonActionsForMobile = this.buttonActions
			? this.buttonActions.filter((action) => action !== "savings")
			: [];
	}

	protected updateAssetWalletsForTransfer(): void {
		const wallets: TradableWallet[] = this.tradableWalletListService.getWalletsByTradableId(
			this.wallet.getCurrencyId()
		);
		if (wallets) {
			this.assetWalletsForTransfer = wallets.length > 1 && this.tradable.isTransferActive();
		}
	}

	// BEST 2.0 opt-in terms
	public onActionClick(action: string, tradableWallet: TradableWallet = null, newTermsAndConditions = false): void {
		if (
			!newTermsAndConditions &&
			(tradableWallet?.getCurrencyId() === TOKEN_BEST_ID || this.tradable?.getId() === TOKEN_BEST_ID) &&
			["buy", "savings", "deposit"].some((a) => a === action) &&
			this.accountService.showBest2TermsOnBestTrade()
		) {
			this.limitOrdersTrackingFacade.updateFlow(OrderFlow.Adp);
			this.handleBest2Terms(action, tradableWallet);
			return;
		}

		const wallet: TradableWallet = tradableWallet ? tradableWallet : this.wallet;
		const offer = new Offer();
		offer.setDirectTradableWalletOffer(true);
		offer.setIndexTrade(wallet instanceof IndexWallet);
		offer.setWalletId(wallet.getId());
		offer.setCurrencyId(wallet.getCurrencyId());

		if (action === TooltipTradableAction.LIMIT_ORDER) {
			this.limitOrdersTrackingFacade.updateFlow(OrderFlow.AdpMore);
			this.limitOrdersService.openOrderTypeDecision(wallet.getCurrencyId());
		}

		if (action === TooltipTradableAction.BUY && !this.userFiatFundsService.hasFiatWalletsWithFunds()) {
			this.fiatDepositEntryService.openDepositNow();
		}

		if (
			(action === TooltipTradableAction.BUY && this.userFiatFundsService.hasFiatWalletsWithFunds()) ||
			action === TooltipTradableAction.SELL
		) {
			this.tradeAction(action, offer);
		} else {
			if (action === TooltipTradableAction.SWAP) {
				if (
					this.tooltipService.isUnderMinSell(this.tradable, wallet, this.fiat) ||
					!this.tradable.isSellActive()
				) {
					this.modalService
						.create(TradeSwapComponent, {
							externalLaunchOptions: {
								wallet,
								usedFor: "right"
							}
						})
						.present();
				} else {
					this.modalService
						.create(TradeSwapComponent, {
							externalLaunchOptions: { wallet }
						})
						.present();
				}
			}

			if (action === TooltipTradableAction.SEND && (!this.tradable || this.tradable.isWithdrawActive())) {
				const sendOffer = { transaction: new SendTransaction() };
				sendOffer.transaction.setWallet(wallet);
				sendOffer.transaction.setDirectTradableWalletOffer(true);
				this.launchDarklyService
					.isActive$(LD_FLAG.WITHDRAWAL_MULTICHAIN)
					.pipe(take(1))
					.subscribe((isEnabled) => {
						if (!isEnabled) {
							this.sendEntryPoint.openCryptoSendModal(sendOffer);
						} else {
							this.sendEntryPoint.openMultichainSendFlow(wallet, this.tradable);
						}
					});
			} else if (action === TooltipTradableAction.TRANSFER && (!this.tradable || this.assetWalletsForTransfer)) {
				const t = new InternalTransaction();
				t.setFrom(wallet.getId());
				t.setSendFlowAccess(false);
				this.modalService.create(TransferModalComponent, { data: t }).present();
			}

			if (action === TooltipTradableAction.DEPOSIT) {
				this.launchDarklyService
					.isActive$(LD_FLAG.DEPOSIT_MULTICHAIN)
					.pipe(take(1))
					.subscribe((isEnabled) => {
						if (isEnabled) {
							this.cryptoDepositFacade.availableCryptoList$.pipe(take(1)).subscribe((list) => {
								const currentDepositCrypto = list.find(
									(item) => item.symbol.toUpperCase() === this.tradable.getAssetSymbol().toUpperCase()
								);
								this.cryptoDepositTrackingFacade.trackDepositFlowStarted();
								if (currentDepositCrypto.networks.length > 1) {
									this.cryptoDepositFacade.navigateToNetworkSelection(currentDepositCrypto.symbol);
								} else {
									this.cryptoDepositFacade.navigateToDepositSummary(
										currentDepositCrypto.symbol,
										currentDepositCrypto.networks[0].id
									);
								}
							});
						} else {
							this.modalService
								.create(CryptowalletDetailsComponent, {
									data: { wallet, cryptocoinIcon: this.tradable.getLogo() }
								})
								.present();
						}
					});
			}

			if (action === TooltipTradableAction.SAVINGS) {
				offer.setType(TRADE_TYPE.RECURRING_BUY);

				const callback: Function = () =>
					this.modalService.create(TradeSavingsComponent, { data: offer }).present();
				if (this.stockTestService.needsTest(offer.getCurrencyId())) {
					this.stockTestHelperService.startTest(callback);
				} else {
					callback();
				}
			}
		}
	}

	// BEST 2.0 opt-in terms
	public handleBest2Terms(action: string, wallet: TradableWallet): void {
		const best2TermsModal = this.modalService.create(Best2TermsModalComponent, null, { showBackdrop: false });
		best2TermsModal.present();
		best2TermsModal.onDismiss.subscribe((res) => {
			if (res.data === "accepted") {
				this.onActionClick(action, wallet);
			}
		});
	}

	public isIndex(item: Tradable): boolean {
		return item instanceof Index;
	}

	/* decoupled from isActionButtonDisabled  */
	public isSendButtonDisabled(): Observable<boolean> {
		if (!this.tradable.isWithdrawActive()) {
			return of(true);
		}

		return this.sendEntryPoint.getSendStatusForCryptoCoin$(this.tradable).pipe(
			startWith(EMPTY),
			map((status: ButtonStatus) => status.isDisabled)
		);
	}

	public getSendButtonTooltip(): Observable<string> {
		return this.sendEntryPoint.getSendStatusForCryptoCoin$(this.tradable).pipe(
			startWith(EMPTY),
			map((status: ButtonStatus) => status.message)
		);
	}

	public onSendClick(wallet: TradableWallet = this.wallet): void {
		const sendOffer = new SendTransaction();
		sendOffer.setWallet(wallet);
		sendOffer.setDirectTradableWalletOffer(true);
		this.launchDarklyService
			.isActive$(LD_FLAG.WITHDRAWAL_MULTICHAIN)
			.pipe(take(1))
			.subscribe((isEnabled) => {
				if (!isEnabled) {
					this.sendEntryPoint.openCryptoSendModal({ transaction: sendOffer });
				} else {
					this.sendTrackingFacade.trackSendFlowStarted();
					this.sendEntryPoint.openMultichainSendFlow(wallet, this.tradable);
				}
			});
	}

	public isActionButtonDisabled(action: string, tradable: Tradable = null): boolean {
		if (
			(action === TooltipTradableAction.BUY || action === TooltipTradableAction.SWAP) &&
			this.digiSunsetHideDetails()
		) {
			return true;
		}
		if (action === "savings") {
			return this.accountService.account.getSavingsDisabled();
		}

		if (action === TooltipTradableAction.LIMIT_ORDER) {
			return !this.tradable.getAssetSettings().isPriceOrderAvailable();
		}

		if (action === TRADE_TYPE.UNSTAKE) {
			if (!tradable) {
				return true;
			}

			let isStaking: boolean = false;
			this.stakedAssetFacade
				.getAdpStakeable$()
				.pipe(
					filter((stakeable: Stakeable) => !!stakeable),
					take(1)
				)
				.subscribe((stakeable: Stakeable) => (isStaking = +stakeable.stakedAmount > 0));

			return !isStaking;
		}

		if (action === TooltipTradableAction.LEVERAGE) {
			let isBtnActive: boolean = true;
			this.statsigClientService
				.isFeatureActive(FEAT_GATES.MARGIN_TRADING_BUY)
				.pipe(take(1))
				.subscribe((isFlagActive) => (isBtnActive = isFlagActive));
			return !isBtnActive;
		}

		if (action === TooltipTradableAction.SWAP && this.isAssetDetailPage()) {
			const wallets = this.tradableWalletListService.getWalletsByTradableId(this.tradable.getId());
			if (!wallets || !this.hasEnoughAssetsToSwap) {
				return true;
			}

			return this.tooltipService.isDisabled(
				action,
				this.tradable,
				this.fiat,
				wallets.find((wallet) => wallet.isDefault())
			);
		}

		return this.tooltipService.isDisabled(action, this.tradable, this.fiat, this.wallet);
	}

	public getTooltip(action: string): string {
		const tooltipOptions: TooltipCoinOptions = {
			action: action as TooltipTradableAction,
			tradable: this.tradable,
			fiat: this.fiat,
			tradableWallet: this.wallet,
			isStaking: +this.stakeable?.stakedAmount > 0
		};

		return this.getTooltipString(tooltipOptions);
	}

	public amountForButton(action: string): string {
		const tooltipOptions: TooltipCoinOptions = {
			action: action as TooltipTradableAction,
			tradable: this.tradable,
			fiat: this.fiat
		};

		return this.tooltipService.getAmountForTooltip(tooltipOptions);
	}

	public showMoreActions(): boolean {
		if (!this.tradable || this.buttonMoreActions.length === 0) {
			return false;
		}

		const hasEnabledActions = this.buttonMoreActions.some(
			(item) => !this.isActionButtonDisabled(item, this.tradable)
		);
		const hasOrderOption = this.buttonMoreActions.includes(TooltipTradableAction.LIMIT_ORDER);
		if (this.marginTradingFacade.showMarginTradingRedDot$.getValue() === null) {
			this.marginTradingFacade.setShowMarginTradingRedDot(
				this.buttonMoreActions.includes(TooltipTradableAction.LEVERAGE)
			);
		}

		return hasEnabledActions || hasOrderOption;
	}

	private hideDepositInMoreButtons(buttonDisabled: boolean): boolean {
		if (!this.tradable || this.buttonMoreActions.length === 0) {
			return true;
		}
		return buttonDisabled;
	}

	private getDepositButtonData$(): Observable<DepositButtonData> {
		return this.isDepositActionButtonDisabled(this.tradable).pipe(
			map((buttonDisabled) => {
				return {
					depositTooltip: this.getDepositButtonTooltip(buttonDisabled),
					depositButtonDisabled: buttonDisabled,
					hideDepositInMoreButtons: this.hideDepositInMoreButtons(buttonDisabled)
				};
			})
		);
	}

	private getDepositButtonTooltip(disabled: boolean): string {
		if (!disabled) {
			return "";
		}
		return this.tooltipService.getTooltip({
			action: "deposit" as TooltipTradableAction,
			tradable: this.tradable,
			fiat: this.fiat,
			tradableWallet: this.wallet,
			isStaking: +this.stakeable?.stakedAmount > 0
		});
	}

	protected readonly TooltipTradableAction = TooltipTradableAction;

	public isLeveragedToken(): boolean {
		return this.tradable instanceof LeveragedToken;
	}
}

interface DepositButtonData {
	depositTooltip: string;
	depositButtonDisabled: boolean;
	hideDepositInMoreButtons: boolean;
}
